import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, apiError, assetUrl } from "../../lib/api";

const humanize = (k) => String(k).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function MediaUpload({ value, onChange, accept, multiple, token, testId, kind = "screenshots" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    const paths = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await http.post("/admin/upload", fd, { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 });
        paths.push(data.path);
      } catch (e) { toast.error(apiError(e)); }
    }
    setBusy(false);
    if (paths.length) { onChange(multiple ? [...(value || []), ...paths] : paths[0]); toast.success("Uploaded — remember to Save changes"); }
  };
  const items = multiple ? value || [] : value ? [value] : [];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {items.map((p) => (
          <div key={p} className="relative overflow-hidden rounded-lg border border-white/10">
            {/\.(mp4|webm|mov)$/i.test(p) ? <video src={assetUrl(p)} preload="metadata" className="h-24 w-16 object-cover" muted /> : <img src={assetUrl(p)} alt="" className="h-20 w-28 object-contain" />}
            <button type="button" aria-label="Remove" onClick={() => onChange(multiple ? items.filter((x) => x !== p) : "")} className="absolute right-1 top-1 rounded-full bg-black/70 p-1"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      <input ref={ref} type="file" accept={accept} multiple={multiple} hidden onChange={(e) => upload([...e.target.files])} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId} className="btn-ghost !py-2 !px-4 !text-xs">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {busy ? "Uploading…" : `Upload ${kind}`}
      </button>
    </div>
  );
}

export function JsonField({ name, value, onChange, token, path = "" }) {
  const id = `cms-${path}`.replace(/[^a-z0-9-]/gi, "-");
  const label = <span className="mb-1.5 block text-xs font-medium text-slate-400">{humanize(name)}</span>;
  if (name === "videos") return <div>{label}<MediaUpload value={value} onChange={onChange} accept="video/mp4,video/webm,video/quicktime" multiple kind="videos" token={token} testId={`${id}-upload`} /></div>;
  if (name === "logo") return <div>{label}<MediaUpload value={value} onChange={onChange} accept="image/*" kind="logo" token={token} testId={`${id}-upload`} /></div>;
  if (name === "screenshots") return <div>{label}<MediaUpload value={value} onChange={onChange} accept="image/*" multiple token={token} testId={`${id}-upload`} /></div>;
  if (typeof value === "boolean") return (
    <label className="flex items-center gap-3 rounded-lg border border-amber-300/20 bg-amber-300/[0.04] p-3 text-sm text-white">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#0088FF]" data-testid={id} />
      {name === "figures_confirmed" ? "I confirm these revenue figures are accurate, properly attributed and supported by records" : humanize(name)}
    </label>
  );
  if (typeof value === "number") return <label className="block">{label}<input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} className="field !py-2.5 !text-sm" data-testid={id} /></label>;
  if (typeof value === "string") {
    const long = value.length > 70 || ["description", "disclosure", "quote"].includes(name);
    return <label className="block">{label}{long
      ? <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="field !py-2.5 !text-sm" data-testid={id} />
      : <input value={value} onChange={(e) => onChange(e.target.value)} className="field !py-2.5 !text-sm" data-testid={id} />}</label>;
  }
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) return (
    <label className="block">{label}<textarea rows={Math.max(3, value.length + 1)} value={value.join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))}
      className="field !py-2.5 !text-sm" data-testid={id} /><span className="mt-1 block text-[11px] text-slate-500">One item per line</span></label>
  );
  if (Array.isArray(value)) return (
    <fieldset>{label}<div className="space-y-3">{value.map((item, i) => (
      <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-wider text-cyan-300">{item?.name || item?.business || item?.label || `${humanize(name)} ${i + 1}`}</p>
        {JsonField({ name: "", value: item, token, path: `${path}-${i}`, onChange: (v) => onChange(value.map((x, j) => (j === i ? v : x))) })}
      </div>
    ))}</div></fieldset>
  );
  if (value && typeof value === "object") return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Object.entries(value).map(([k, v]) => (
        <div key={k} className={typeof v === "object" || (typeof v === "string" && v.length > 70) || k === "description" ? "sm:col-span-2" : ""}>
          {JsonField({ name: k, value: v, token, path: `${path}-${k}`, onChange: (nv) => onChange({ ...value, [k]: nv }) })}
        </div>
      ))}
    </div>
  );
  return null;
}
